import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { album } from "@/data/album";

type RangeKey = "24h" | "7d" | "28d" | "12mo" | "all";

type OverviewAlbum = { streams: number; uniqueListeners: number };

type OverviewTrackRow = { trackId: string; streams: number; uniqueListeners: number };

type OverviewResponse = {
  range?: RangeKey;
  album?: OverviewAlbum;
  tracks?: OverviewTrackRow[];
  addToHomeScreenUniqueLeads?: number;
  uniqueLeadsLoggedIn?: number;
  purchaseCount?: number;
  error?: string;
};

const RANGE_LABELS: Record<RangeKey, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "28d": "28 days",
  "12mo": "12 months",
  all: "All time",
};

const ALBUM_TITLE = "Can I Evolve?";
const ALBUM_ART_SRC = "/assets/evolve.jpeg";

export function AdminStreamingStatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo((): RangeKey => {
    const r = searchParams.get("range");
    if (r === "24h" || r === "7d" || r === "28d" || r === "12mo" || r === "all") return r;
    return "24h";
  }, [searchParams]);

  const setRange = (next: RangeKey) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("range", next);
        return p;
      },
      { replace: true },
    );
  };

  const [checking, setChecking] = useState(true);
  const [overview, setOverview] = useState<{
    album: OverviewAlbum;
    tracks: OverviewTrackRow[];
    addToHomeScreenUniqueLeads: number;
    uniqueLeadsLoggedIn: number;
    purchaseCount: number;
  } | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/session", {
          method: "GET",
          credentials: "same-origin",
        });
        const data = (await res.json()) as { authenticated?: boolean };
        if (cancelled) return;
        if (!res.ok || !data.authenticated) {
          navigate("/admin/login", { replace: true });
          return;
        }
      } catch {
        if (!cancelled) navigate("/admin/login", { replace: true });
        return;
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (checking) return;
    let cancelled = false;
    (async () => {
      setOverviewLoading(true);
      setOverviewError(null);
      try {
        const res = await fetch(`/api/admin/overview?range=${range}`, {
          credentials: "same-origin",
        });
        const data = (await res.json()) as OverviewResponse;
        if (cancelled) return;
        if (!res.ok) {
          setOverview(null);
          setOverviewError(data.error ?? "Failed to load stats");
          return;
        }
        const albumStats = data.album;
        const trackRows = data.tracks;
        setOverview({
          album: {
            streams: typeof albumStats?.streams === "number" ? albumStats.streams : 0,
            uniqueListeners:
              typeof albumStats?.uniqueListeners === "number" ? albumStats.uniqueListeners : 0,
          },
          tracks: Array.isArray(trackRows) ? trackRows : [],
          addToHomeScreenUniqueLeads:
            typeof data.addToHomeScreenUniqueLeads === "number"
              ? data.addToHomeScreenUniqueLeads
              : 0,
          uniqueLeadsLoggedIn:
            typeof data.uniqueLeadsLoggedIn === "number" ? data.uniqueLeadsLoggedIn : 0,
          purchaseCount: typeof data.purchaseCount === "number" ? data.purchaseCount : 0,
        });
      } catch {
        if (!cancelled) {
          setOverview(null);
          setOverviewError("Failed to load stats");
        }
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checking, range]);

  const trackStatsById = useMemo(() => {
    const m = new Map<string, OverviewTrackRow>();
    for (const row of overview?.tracks ?? []) {
      m.set(row.trackId, row);
    }
    return m;
  }, [overview?.tracks]);

  const albumStreamsCell =
    overviewError !== null ? (
      <span className="text-red-400 text-sm font-normal">{overviewError}</span>
    ) : overviewLoading || overview === null ? (
      "…"
    ) : (
      <Link
        to={`/admin/streams?range=${encodeURIComponent(range)}`}
        className="text-primary underline-offset-2 hover:underline"
      >
        {overview.album.streams.toLocaleString()}
      </Link>
    );

  const albumListenersCell =
    overviewError !== null ? (
      <span className="text-red-400 text-sm font-normal">{overviewError}</span>
    ) : overviewLoading || overview === null ? (
      "…"
    ) : (
      <Link
        to={`/admin/listeners?range=${encodeURIComponent(range)}`}
        className="text-primary underline-offset-2 hover:underline"
      >
        {overview.album.uniqueListeners.toLocaleString()}
      </Link>
    );

  const addToHomeTile =
    overviewError !== null ? (
      <span className="text-red-400 text-sm font-normal">{overviewError}</span>
    ) : overviewLoading || overview === null ? (
      "…"
    ) : (
      <span className="text-2xl font-semibold tabular-nums text-white">
        {overview.addToHomeScreenUniqueLeads.toLocaleString()}
      </span>
    );

  const uniqueLoginsTile =
    overviewError !== null ? (
      <span className="text-red-400 text-sm font-normal">{overviewError}</span>
    ) : overviewLoading || overview === null ? (
      "…"
    ) : (
      <span className="text-2xl font-semibold tabular-nums text-white">
        {overview.uniqueLeadsLoggedIn.toLocaleString()}
      </span>
    );

  const purchasesTile =
    overviewError !== null ? (
      <span className="text-red-400 text-sm font-normal">{overviewError}</span>
    ) : overviewLoading || overview === null ? (
      "…"
    ) : (
      <span className="text-2xl font-semibold tabular-nums text-white">
        {overview.purchaseCount.toLocaleString()}
      </span>
    );

  if (checking) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white bg-black">
      <div className="px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-3xl mx-auto space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-end">
          <label className="text-sm">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as RangeKey)}
              aria-label="Time range"
              className="rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                <option key={key} value={key} className="bg-black">
                  {RANGE_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-4">
            <p className="text-gray-400 text-sm font-medium">Purchases</p>
            <div className="mt-1">{purchasesTile}</div>
          </div>
          <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-4">
            <p className="text-gray-400 text-sm font-medium">Logins</p>
            <div className="mt-1">{uniqueLoginsTile}</div>
          </div>
          <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-4">
            <p className="text-gray-400 text-sm font-medium">Home</p>
            <div className="mt-1">{addToHomeTile}</div>
          </div>
        </div>

        <div className="rounded-xl border border-white/15 bg-white/5 overflow-hidden">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-white/15 text-gray-400">
                <th scope="col" className="py-3 px-4 font-medium">
                  Track
                </th>
                <th scope="col" className="py-3 px-4 font-medium text-right tabular-nums w-[7rem] sm:w-[8rem]">
                  Streams
                </th>
                <th scope="col" className="py-3 px-4 font-medium text-right tabular-nums w-[7rem] sm:w-[8rem]">
                  Listeners
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10 bg-white/[0.04]">
                <td className="py-4 px-4 align-middle">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={ALBUM_ART_SRC}
                      alt=""
                      className="w-14 h-14 rounded-md object-cover shrink-0 border border-white/10"
                    />
                    <span className="font-semibold text-white leading-snug">{ALBUM_TITLE}</span>
                  </div>
                </td>
                <td className="py-4 px-4 align-middle text-right tabular-nums text-base font-semibold">
                  {albumStreamsCell}
                </td>
                <td className="py-4 px-4 align-middle text-right tabular-nums text-base font-semibold">
                  {albumListenersCell}
                </td>
              </tr>
              {album.map((song) => {
                const row = trackStatsById.get(song.trackId);
                const streamsCount =
                  overviewError !== null
                    ? null
                    : overviewLoading || overview === null
                      ? null
                      : row != null
                        ? row.streams
                        : 0;
                const streamsCell =
                  streamsCount === null ? (
                    overviewError !== null ? (
                      "—"
                    ) : (
                      "…"
                    )
                  ) : (
                    <Link
                      to={`/admin/streams?range=${encodeURIComponent(range)}&trackId=${encodeURIComponent(song.trackId)}`}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {streamsCount.toLocaleString()}
                    </Link>
                  );
                const listenersCell =
                  overviewError !== null ? (
                    "—"
                  ) : overviewLoading || overview === null ? (
                    "…"
                  ) : row != null ? (
                    <Link
                      to={`/admin/listeners?range=${encodeURIComponent(range)}&trackId=${encodeURIComponent(song.trackId)}`}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {row.uniqueListeners.toLocaleString()}
                    </Link>
                  ) : (
                    "0"
                  );
                return (
                  <tr key={song.id} className="border-b border-white/[0.06] last:border-b-0">
                    <td className="py-3 px-4 text-gray-200 align-middle">
                      <div className="flex min-w-0 items-baseline gap-x-2">
                        <span className="shrink-0 text-right tabular-nums text-gray-500">
                          {String(song.id).padStart(2, "0")}
                        </span>
                        <span className="min-w-0 leading-snug">{song.title}</span>
                      </div>
                    </td>
                    <td
                      className={`py-3 px-4 text-right tabular-nums align-middle ${
                        overviewError === null && !overviewLoading && row != null
                          ? "text-gray-200"
                          : streamsCount !== null && overviewError === null
                            ? ""
                            : "text-gray-500"
                      }`}
                    >
                      {streamsCell}
                    </td>
                    <td
                      className={`py-3 px-4 text-right tabular-nums align-middle ${
                        overviewError === null && !overviewLoading && row != null
                          ? "text-gray-200"
                          : "text-gray-500"
                      }`}
                    >
                      {listenersCell}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
